import React, { useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import { Navigation } from "swiper/modules";
import { ChevronLeft } from "lucide-react";
import {
  cn,
  dateFormater,
  groupDatesByMonthBook,
} from "@/lib/utils";

type AvailableDate = {
  id: string;
  start_date: Date;
  end_date: Date;
  price: number;
  expiration_date_time: string | null;
  available_seats?: number;
  batchId: string;
};

interface DateGroupSliderProps {
  availableDates: AvailableDate[];
}

const GroupDateShowSlider: React.FC<DateGroupSliderProps> = ({
  availableDates,
}) => {
  const groupedDates = useMemo(
    () => groupDatesByMonthBook(availableDates),
    [availableDates],
  );

  const monthGroups = useMemo(() => {
    const entries = Object.entries(groupedDates);
    return entries.reduce(
      (acc, curr, idx) => {
        if (idx % 2 === 0) {
          acc.push([curr]);
        } else {
          // @ts-expect-error because we know the last element is an array
          acc[acc.length - 1].push(curr);
        }
        return acc;
      },
      [] as [string, AvailableDate[]][][],
    );
  }, [groupedDates]);

  return (
    <div>
      {monthGroups.length > 0 ? (
        <div className="relative select-none rounded-lg border border-zinc-400 p-4 xl:pt-0">
          <Swiper
            breakpoints={{
              200: { slidesPerView: 1 },
            }}
            spaceBetween={12}
            navigation={{
              prevEl: `.trip-booking-date-prev`,
              nextEl: `.trip-booking-date-next`,
            }}
            modules={[Navigation]}
          >
            {monthGroups.map((group, index) => (
              <div key={index}>
                {group.map(([monthYear, dates]) => (
                  <SwiperSlide key={monthYear}>
                    <MonthGroup monthYear={monthYear} dates={dates} />
                  </SwiperSlide>
                ))}
              </div>
            ))}
          </Swiper>
          <NavigationButtons />
        </div>
      ) : (
        <p>No available dates.</p>
      )}
    </div>
  );
};

const MonthGroup: React.FC<{ monthYear: string; dates: AvailableDate[] }> =
  React.memo(({ monthYear, dates }) => (
    <div className="p-0 xl:p-4">
      <h2 className="mb-8 text-center text-base font-medium text-primary-text sm:mb-11 md:text-lg">
        {monthYear}
      </h2>
      <div className="grid grid-cols-2 gap-2 gap-y-5 sm:grid-cols-3 md:grid-cols-4 xl:gap-5">
        {dates.map((date, i) => (
          <DateCard key={i} date={date} />
        ))}
      </div>
    </div>
  ));

MonthGroup.displayName = "MonthGroup";

const DateCard: React.FC<{ date: AvailableDate }> = ({ date }) => {
  // Check if date is expired
  const isExpired = date.expiration_date_time 
    ? new Date() > new Date(date.expiration_date_time)
    : false;

  return (
    <div className="flex">
      <label
        htmlFor={date.start_date?.toString()}
        className={cn(
          "relative w-full cursor-pointer rounded-md border",
          (!date.available_seats || isExpired)
            ? "cursor-not-allowed border-primary-text/45"
            : "cursor-pointer border-primary-text/75",
          "bg-white",
        )}
      >
        <div className="absolute left-1/2 top-0 flex w-full max-w-[80%] -translate-x-1/2 mb-2 -translate-y-1/2 flex-col items-center leading-none">
          {(date.available_seats && !isExpired) ? (
            <div
              className={`w-full rounded-full border border-primary-text/25 py-1.5 text-center text-xs font-medium leading-none tracking-wider sm:py-2 xl:text-sm xl:leading-none ${date.available_seats <= 10 ? "bg-[#EFCA08] text-black" : "bg-green-500 text-white"}`}
            >
              {date.available_seats <= 10 ? "Few Lefts" : "Available"}
            </div>
          ) : (
            <p className="w-full rounded-full border border-primary-text/25 bg-red-500 py-1.5 text-center text-xs font-medium leading-tight tracking-wider text-white sm:py-2 xl:text-sm">
              Sold Out
            </p>
          )}
        </div>
        <div
          className={`mb-2 mt-[1.125rem] text-center text-base sm:mb-2.5 sm:mt-8 md:text-lg ${(!date.available_seats || isExpired) ? "text-primary-text/55" : "text-gray-800"}`}
        >
          <span>
            {dateFormater({ date: date.start_date, formatString: "dd MMM" })}
          </span>
          {" - "}
          <span>
            {dateFormater({ date: date.end_date, formatString: "dd MMM" })}
          </span>
        </div>
      </label>
    </div>
  );
};

const NavigationButtons: React.FC = () => (
  <>
    <button className="trip-booking-date-prev absolute left-4 top-4 z-20 h-6 w-6 rounded-full bg-neutral-200 p-0.5 text-primary-text hover:bg-custom-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-neutral-200 disabled:hover:text-primary-text xl:top-5">
      <ChevronLeft className="h-full w-full" />
    </button>
    <button className="trip-booking-date-next absolute right-4 top-4 z-20 h-6 w-6 rounded-full bg-neutral-200 p-0.5 text-primary-text hover:bg-custom-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-neutral-200 disabled:hover:text-primary-text xl:top-5">
      <ChevronLeft className="h-full w-full rotate-180" />
    </button>
  </>
);

export default GroupDateShowSlider;
